import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
  Avatar, Box, Button, Heading, Text, Container, Icon,
  useToast, useMediaQuery, Tabs, TabList, TabPanels, Tab, TabPanel,
  Badge, Flex, VStack, HStack,
} from "@chakra-ui/react";
import { Select as SearchableSelect } from "chakra-react-select";
import { useAuth } from "./providers/AuthProvider";
import { motion } from "framer-motion";
import moment from "moment";
import {
  FiHome, FiBook, FiFileText, FiDollarSign, FiCreditCard, FiBell, FiMessageCircle,
  FiEye, FiX,
} from "react-icons/fi";
import { getGreetingName, getParticipantFullName, getTimeOfDayGreeting } from "./utils/formatHelpers";
import {
  getStaffPreviewAccess,
  fetchEnrolledStudentsForPreview,
  getStoredViewAsStudent,
  setStoredViewAsStudent,
  clearStoredViewAsStudent,
} from "./utils/staffHelpers";
import { DashboardTab } from "./components/DashboardTab";
import { CoursesTab } from "./components/CoursesTab";
import { DocumentsTab } from "./components/DocumentsTab";
import {
  buildPortalDocumentsList,
  openPortalDocument,
} from "./utils/programDocuments";
import { InvoicesTab } from "./components/InvoicesTab";
import { PaymentsTab } from "./components/PaymentsTab";
import { NoticesTab } from "./components/NoticesTab";
import { ContactTab } from "./components/ContactTab";
import { getPdfViewUrl } from "./components/portalUi";
import { COLORS } from "./styles/colors";
import { getPhotoUrlFromAdditionalData } from "./utils/profilePhoto";

const NAV_ITEMS = [
  { label: "Home", short: "Home", icon: FiHome },
  { label: "My Courses", short: "Courses", icon: FiBook },
  { label: "Documents", short: "Docs", icon: FiFileText },
  { label: "Invoices", short: "Invoices", icon: FiDollarSign },
  { label: "Payments", short: "Pay", icon: FiCreditCard },
  { label: "Notices", short: "Notices", icon: FiBell },
  { label: "Contact Us", short: "Contact", icon: FiMessageCircle },
];

export const EnrolledCourses = () => {
  const {user} = useAuth();
  const [certifications, setCertifications] = useState([]);
  const [modules, setModules] = useState({});
  const [invoices, setInvoices] = useState({});
  const [documents, setDocuments] = useState({});
  const [payments, setPayments] = useState({});
  const [registrationMap, setRegistrationMap] = useState({});
  const [participantNameForGreeting, setParticipantNameForGreeting] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadedDocs, setDownloadedDocs] = useState(new Set());
  const [downloadingAll, setDownloadingAll] = useState({});
  const [canPreviewAsStudent, setCanPreviewAsStudent] = useState(false);
  const [studentOptions, setStudentOptions] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  // null = own portal; otherwise view that student's enrollments (read-only, no DB writes)
  const [viewAsStudent, setViewAsStudent] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [navFade, setNavFade] = useState({ left: false, right: false });
  const navScrollRef = useRef(null);
  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  const updateNavFade = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setNavFade({
      left: scrollLeft > 6,
      right: scrollLeft + clientWidth < scrollWidth - 6,
    });
  }, []);

  useEffect(() => {
    updateNavFade();
    window.addEventListener("resize", updateNavFade);
    return () => window.removeEventListener("resize", updateNavFade);
  }, [updateNavFade, tabIndex]);

  // Keep the active mobile tab visible (avoids feeling "cut off")
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const active = el.querySelector('[aria-selected="true"]');
    if (active?.scrollIntoView) {
      active.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
    // Recalc fades after scroll settles
    const t = setTimeout(updateNavFade, 280);
    return () => clearTimeout(t);
  }, [tabIndex, updateNavFade]);

  // --- Invoice view ---
  const handleViewInvoice = (invoice) => {
    const url = getPdfViewUrl(invoice);
    if (!url) {
      toast({ title: "PDF Not Available", description: "This invoice doesn't have a PDF available yet.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // --- Document view/download ---
  const viewDocument = (doc) => {
    openPortalDocument(doc);
  };

  const downloadDocumentAsPdf = async (doc, suppressToast = false) => {
    setDownloadingDoc(doc.id || doc.title);
    try {
      if (doc.isPdf && doc.pdfDownloadUrl) {
        const link = document.createElement('a');
        link.href = doc.pdfDownloadUrl;
        link.download = `${doc.title}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloadedDocs(prev => new Set([...prev, doc.id || doc.title]));
        if (!suppressToast) {
          const displayName = doc.title ? doc.title.replace(/\s*-\s*.*$/, '').trim() : '';
          toast({ title: "Download Started", description: `Downloading ${displayName}...`, status: "success", duration: 3000, isClosable: true });
        }
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      toast({ title: "Download Error", description: "Failed to download document.", status: "error", duration: 3000, isClosable: true });
    } finally {
      setDownloadingDoc(null);
    }
  };

  const downloadAllDocumentsForCert = async (certId) => {
    setDownloadingAll(prev => ({ ...prev, [certId]: true }));
    try {
      const certDocs = documents[certId] || [];
      const completedPdfs = certDocs.filter(d => d.status === 'done' && d.isPdf && d.pdfDownloadUrl && !downloadedDocs.has(d.id || d.title));
      if (completedPdfs.length === 0) {
        toast({ title: "No PDFs Available", description: "No new PDFs to download.", status: "info", duration: 3000, isClosable: true });
        return;
      }
      for (let i = 0; i < completedPdfs.length; i++) {
        if (i > 0) await new Promise(resolve => setTimeout(resolve, 2500));
        await downloadDocumentAsPdf(completedPdfs[i], true);
      }
      toast({ title: "All Downloads Started", description: `Downloaded ${completedPdfs.length} PDF file(s).`, status: "success", duration: 3000, isClosable: true });
    } finally {
      setDownloadingAll(prev => ({ ...prev, [certId]: false }));
    }
  };

  // --- Fetch invoices ---
  const fetchInvoicesForCertification = async (certId, registrationIds) => {
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, invoice_line_items(*)")
        .in("registration_id", registrationIds)
        .order("invoice_date", { ascending: true });
      if (error) throw error;
      setInvoices(prev => ({ ...prev, [certId]: data || [] }));
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  // --- Fetch enrollment documents (filtered by TMS program_document_templates when configured) ---
  const fetchDocumentsForCertification = async (certId, registrationIds, program = null) => {
    try {
      const { documentsList } = await buildPortalDocumentsList(supabase, {
        registrationIds,
        programType: program?.programType || 'full_certification',
        programCode: program?.programCode || null,
      });

      setDocuments(prev => ({ ...prev, [certId]: documentsList }));
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  // --- Fetch payments ---
  const fetchPaymentsForCertification = async (certId, registrationIds) => {
    try {
      const { data, error } = await supabase
        .from("payments")
        .select("id, registration_id, invoice_id, payment_method, amount, currency, status, payment_provider_ref, paid_at, created_at, notes, receipt_number, pdf_file_id, pdf_url, receipt_generated_at")
        .in("registration_id", registrationIds)
        .order("paid_at", { ascending: false });
      if (error) throw error;
      setPayments(prev => ({ ...prev, [certId]: data || [] }));
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };

  const resetPortalData = () => {
    setCertifications([]);
    setModules({});
    setInvoices({});
    setDocuments({});
    setPayments({});
    setRegistrationMap({});
    setParticipantNameForGreeting(null);
    setProfilePhotoUrl(null);
    setDownloadedDocs(new Set());
  };

  // --- Main data fetch (own account, or selected student when previewing) ---
  const getAllEnrolledCourses = async () => {
    try {
      setLoading(true);
      setInvoices({});
      setDocuments({});
      setPayments({});

      const targetId = viewAsStudent?.userId || (!viewAsStudent ? user?.id : null);
      const targetEmail = viewAsStudent?.email || (!viewAsStudent ? user?.email : null);

      if (!targetEmail && !targetId) {
        resetPortalData();
        setLoading(false);
        return;
      }

      // Merge by registration id: look up by user_id AND email.
      // Email-only rows (user_id not linked yet) must still appear alongside linked ones.
      const registrationSelect =
        "id, draft_cert_run_code, registration_stage, participant_email, user_id, participant_name, additional_data, created_at";
      const registrationsById = new Map();
      let regError = null;

      if (targetId) {
        const { data: dataById, error: errorById } = await supabase
          .from("registration")
          .select(registrationSelect)
          .eq("user_id", targetId);
        if (errorById) {
          regError = errorById;
        } else {
          (dataById || []).forEach((r) => registrationsById.set(r.id, r));
        }
      }

      if (targetEmail) {
        const { data: dataByEmail, error: errorByEmail } = await supabase
          .from("registration")
          .select(registrationSelect)
          .ilike("participant_email", targetEmail);
        if (errorByEmail) {
          regError = errorByEmail;
        } else {
          (dataByEmail || []).forEach((r) => {
            if (!registrationsById.has(r.id)) registrationsById.set(r.id, r);
          });
        }
      }

      if (regError) throw regError;
      const registrations = Array.from(registrationsById.values());

      const nameFromRegistration = registrations
        .map((r) => r.participant_name)
        .find((n) => n && String(n).trim());
      setParticipantNameForGreeting(
        viewAsStudent?.name
          || (nameFromRegistration ? String(nameFromRegistration).trim() : null)
      );

      const latestWithPhoto = [...registrations]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .map((r) => getPhotoUrlFromAdditionalData(r.additional_data))
        .find(Boolean);
      setProfilePhotoUrl(latestWithPhoto || null);

      const certToRegMap = {};
      registrations.forEach(r => {
        if (r.draft_cert_run_code && r.registration_stage === "payment_received") {
          if (!certToRegMap[r.draft_cert_run_code]) certToRegMap[r.draft_cert_run_code] = [];
          certToRegMap[r.draft_cert_run_code].push(r.id);
        }
      });
      setRegistrationMap(certToRegMap);

      const certificationIds = Object.keys(certToRegMap);
      if (certificationIds.length === 0) {
        setCertifications([]);
        setModules({});
        setLoading(false);
        return;
      }

      const { data: certificationData, error: certError } = await supabase
        .from("full_certification_runs")
        .select(`id, run_name, intake, start_date, end_date, full_certification (code, name, description, cost)`)
        .in("id", certificationIds);
      if (certError) throw certError;

      const { data: moduleData, error: moduleError } = await supabase
        .from("full_cert_many_course_run")
        .select(`cert_run_code, course_run (id, course_type_code, start_date, end_date, venue, venue_id, room_id, instructor_name, trainer_id, trainers (name, email), venues (name, address), rooms (name, floor, unit), course_type (code, name, course_resources, assessment_resources))`)
        .in("cert_run_code", certificationIds);
      if (moduleError) throw moduleError;

      const courseRunIds = [];
      (moduleData || []).forEach(m => { if (m.course_run?.id) courseRunIds.push(m.course_run.id); });

      let sessionsData = [];
      if (courseRunIds.length > 0) {
        const { data: allSessions, error: sessionsError } = await supabase
          .from("session").select("*").in("course_run_code", courseRunIds).order("session_date", { ascending: true });
        if (!sessionsError) sessionsData = allSessions || [];
      }

      const venueIds = [...new Set(sessionsData?.filter(s => s.venue_id && !s.custom_venue).map(s => s.venue_id) || [])];
      const roomIds = [...new Set(sessionsData?.filter(s => s.room_id && !s.custom_room).map(s => s.room_id) || [])];
      const venueMap = {};
      if (venueIds.length > 0) { const { data: venues } = await supabase.from("venues").select("code, name, address, is_online").in("code", venueIds); venues?.forEach(v => { venueMap[v.code] = v; }); }
      const roomMap = {};
      if (roomIds.length > 0) { const { data: rooms } = await supabase.from("rooms").select("code, name, floor, unit").in("code", roomIds); rooms?.forEach(r => { roomMap[r.code] = r; }); }

      const sessionsByCourseRun = {};
      sessionsData.forEach(s => { if (!sessionsByCourseRun[s.course_run_code]) sessionsByCourseRun[s.course_run_code] = []; sessionsByCourseRun[s.course_run_code].push(s); });

      (moduleData || []).forEach(module => {
        if (module.course_run?.id) {
          const sessions = sessionsByCourseRun[module.course_run.id] || [];
          module.course_run.course_dates = sessions.map(session => {
            const venueObj = session.venue_id && venueMap[session.venue_id];
            return {
              date: session.session_date,
              start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
              end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
              room: session.custom_room || (session.room_id && roomMap[session.room_id]?.name) || session.room_id || "",
              venue: session.custom_venue || (venueObj?.name) || session.venue_id || "TBC",
              venueAddress: venueObj?.is_online ? "" : (venueObj?.address || ""),
              venue_id: session.venue_id, room_id: session.room_id,
              session_id: session.session_id || session.id,
              meeting_url: session.meeting_url || "",
              is_online: !!venueObj?.is_online || !!session.meeting_url,
            };
          });
        }
      });

      const modMap = {};
      moduleData.forEach(m => { const cId = m.cert_run_code; if (!modMap[cId]) modMap[cId] = []; modMap[cId].push(m.course_run); });

      setModules(modMap);
      setCertifications(certificationData || []);

      const certById = {};
      (certificationData || []).forEach((c) => { certById[c.id] = c; });

      for (const certId of Object.keys(certToRegMap)) {
        const programCode = certById[certId]?.full_certification?.code || null;
        await fetchInvoicesForCertification(certId, certToRegMap[certId]);
        await fetchDocumentsForCertification(certId, certToRegMap[certId], {
          programType: 'full_certification',
          programCode,
        });
        await fetchPaymentsForCertification(certId, certToRegMap[certId]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching courses:", error);
      resetPortalData();
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadPreviewAccess = async () => {
      if (!user?.id) {
        setCanPreviewAsStudent(false);
        setStudentOptions([]);
        setViewAsStudent(null);
        clearStoredViewAsStudent();
        return;
      }
      const { canPreview } = await getStaffPreviewAccess(user.id);
      if (cancelled) return;
      setCanPreviewAsStudent(canPreview);
      if (!canPreview) {
        setStudentOptions([]);
        setViewAsStudent(null);
        clearStoredViewAsStudent();
        return;
      }
      setLoadingStudents(true);
      const students = await fetchEnrolledStudentsForPreview();
      if (cancelled) return;
      const options = students.map((s) => ({
        value: s.value,
        label: s.label,
        userId: s.userId,
        email: s.email,
        name: s.name,
        runCode: s.runCode,
        programmeName: s.programmeName,
        startDate: s.startDate,
        endDate: s.endDate,
        dateRangeLabel: s.dateRangeLabel,
        statusKind: s.statusKind,
        statusLabel: s.statusLabel,
        searchText: s.searchText,
        secondaryLabel: s.secondaryLabel,
      }));
      setStudentOptions(options);
      setLoadingStudents(false);

      // Restore preview if still valid after leaving Enrolled Courses (e.g. Profile)
      const stored = getStoredViewAsStudent();
      if (stored) {
        const stillValid = options.some((o) => o.value === stored.value);
        if (stillValid) {
          setViewAsStudent(stored);
        } else {
          clearStoredViewAsStudent();
          setViewAsStudent(null);
        }
      }
    };
    loadPreviewAccess();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (user?.email || user?.id) getAllEnrolledCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when identity or preview target changes
  }, [user?.email, user?.id, viewAsStudent?.value]);

  const handleSelectStudent = (option) => {
    if (!option) {
      setViewAsStudent(null);
      clearStoredViewAsStudent();
      return;
    }
    const next = {
      value: option.value,
      userId: option.userId || null,
      email: option.email || null,
      name: option.name || option.label,
    };
    setViewAsStudent(next);
    setStoredViewAsStudent(next);
  };

  const exitStudentPreview = () => {
    setViewAsStudent(null);
    clearStoredViewAsStudent();
  };

  const greetingUser = viewAsStudent
    ? { email: viewAsStudent.email, user_metadata: { full_name: viewAsStudent.name } }
    : user;

  if (loading && !canPreviewAsStudent) {
    return (
      <Box minH="100vh" py={{ base: 4, md: 8 }}>
        <Container maxW="container.lg" px={{ base: 4, md: 6 }}>
          <Text textAlign="center">Loading...</Text>
        </Container>
      </Box>
    );
  }

  const selectedStudentOption = viewAsStudent
    ? studentOptions.find((o) => o.value === viewAsStudent.value) || {
        value: viewAsStudent.value,
        label: viewAsStudent.name,
        ...viewAsStudent,
      }
    : null;

  const filterStudentOption = (option, inputValue) => {
    const q = (inputValue || "").trim().toLowerCase();
    if (!q) return true;
    const haystack = (option.data?.searchText || option.data?.name || option.label || "").toLowerCase();
    return haystack.includes(q);
  };

  const statusBadgeStyles = (kind) => {
    if (kind === "completed") {
      return { bg: "green.100", color: "green.800" };
    }
    if (kind === "in_progress") {
      return { bg: "orange.100", color: "orange.800" };
    }
    if (kind === "upcoming") {
      return { bg: "blue.100", color: "blue.800" };
    }
    return { bg: "gray.100", color: "gray.700" };
  };

  const formatStudentOptionLabel = (option, { context }) => {
    if (context === "value") {
      return (
        <Text as="span" noOfLines={1} fontWeight="600">
          {option.name || option.label}
          {option.runCode ? (
            <Text as="span" fontWeight="500" color="gray.500">
              {` · ${option.runCode}`}
            </Text>
          ) : null}
        </Text>
      );
    }

    return (
      <Box w="100%" minW={0} py={0.5}>
        <Flex align="center" gap={1.5} flexWrap="wrap" mb={option.secondaryLabel ? 0.5 : 0}>
          <Text fontWeight="600" color="gray.800" noOfLines={1}>
            {option.name || option.label}
          </Text>
          {option.statusLabel ? (
            <Badge
              {...statusBadgeStyles(option.statusKind)}
              fontSize="2xs"
              borderRadius="md"
              textTransform="none"
              flexShrink={0}
              px={1.5}
              py={0.5}
              fontWeight="700"
            >
              {option.statusLabel}
            </Badge>
          ) : null}
        </Flex>
        {option.secondaryLabel ? (
          <Text fontSize="xs" color="gray.500" noOfLines={1} lineHeight="short">
            {option.secondaryLabel}
          </Text>
        ) : null}
      </Box>
    );
  };

  const greetingName = getGreetingName(greetingUser, participantNameForGreeting);
  const studentFullName =
    (participantNameForGreeting && String(participantNameForGreeting).trim()) ||
    getParticipantFullName(greetingUser);
  const showCourseContent = !loading && certifications.length > 0;
  // Notices (5) + Contact (6) always available; course tabs need enrollments
  const allowTabContent = showCourseContent || tabIndex >= 5;

  const renderSidebar = () => (
    <Box
      display={{ base: "none", lg: "flex" }}
      flexDirection="column"
      w="248px"
      flexShrink={0}
      bg={COLORS.maastrichtBlue}
      color="white"
      borderRadius="2xl"
      p={4}
      minH="640px"
      position="sticky"
      top="88px"
      alignSelf="flex-start"
      boxShadow="lg"
    >
      <Box mb={5} px={2}>
        <Text fontSize="2xs" color="whiteAlpha.600" fontWeight="700" letterSpacing="0.08em" mb={1.5}>
          STUDENT PORTAL
        </Text>
        <HStack spacing={3} align="center">
          <Avatar
            size="sm"
            name={studentFullName || greetingName}
            src={profilePhotoUrl || (!viewAsStudent ? user?.user_metadata?.avatar_url : undefined)}
            bg="teal.500"
            color="white"
            flexShrink={0}
          />
          <Text fontWeight="700" fontSize="md" noOfLines={1} lineHeight="short" minW={0}>
            {greetingName}
          </Text>
        </HStack>
      </Box>

      <VStack align="stretch" spacing={1} flex="1">
        {NAV_ITEMS.map((item, idx) => {
          const active = tabIndex === idx;
          return (
            <Button
              key={item.label}
              leftIcon={<Icon as={item.icon} boxSize={4} />}
              justifyContent="flex-start"
              variant="ghost"
              color={active ? COLORS.maastrichtBlue : "whiteAlpha.900"}
              bg={active ? "white" : "transparent"}
              _hover={{ bg: active ? "white" : "whiteAlpha.200" }}
              _active={{ bg: active ? "white" : "whiteAlpha.300" }}
              fontWeight={active ? "700" : "500"}
              fontSize="sm"
              h="44px"
              px={3}
              borderRadius="xl"
              borderLeftWidth="3px"
              borderLeftColor={active ? COLORS.mandarin : "transparent"}
              onClick={() => setTabIndex(idx)}
            >
              {item.label}
            </Button>
          );
        })}
      </VStack>
    </Box>
  );

  const tabPanels = (
    <TabPanels>
      <TabPanel px={0} pt={{ base: 1, lg: 0 }}>
        <DashboardTab
          certifications={certifications}
          modules={modules}
          invoices={invoices}
          documents={documents}
          greetingName={greetingName}
          onNavigateTab={setTabIndex}
          registrationMap={registrationMap}
        />
      </TabPanel>
      <TabPanel px={0} pt={{ base: 3, lg: 0 }}>
        <CoursesTab
          certifications={certifications}
          modules={modules}
          isMobile={isMobile}
          toast={toast}
        />
      </TabPanel>
      <TabPanel px={0} pt={{ base: 3, lg: 0 }}>
        <DocumentsTab
          certifications={certifications}
          documents={documents}
          downloadingDoc={downloadingDoc}
          downloadedDocs={downloadedDocs}
          downloadingAll={downloadingAll}
          viewDocument={viewDocument}
          downloadDocumentAsPdf={downloadDocumentAsPdf}
          downloadAllDocumentsForCert={downloadAllDocumentsForCert}
        />
      </TabPanel>
      <TabPanel px={0} pt={{ base: 3, lg: 0 }}>
        <InvoicesTab
          certifications={certifications}
          invoices={invoices}
          handleViewInvoice={handleViewInvoice}
          isMobile={isMobile}
        />
      </TabPanel>
      <TabPanel px={0} pt={{ base: 3, lg: 0 }}>
        <PaymentsTab
          certifications={certifications}
          payments={payments}
          invoices={invoices}
        />
      </TabPanel>
      <TabPanel px={0} pt={{ base: 3, lg: 0 }}>
        <NoticesTab mode="full_cert" certifications={certifications} />
      </TabPanel>
      <TabPanel px={0} pt={{ base: 3, lg: 0 }}>
        <ContactTab
          certifications={certifications}
          studentFullName={studentFullName}
        />
      </TabPanel>
    </TabPanels>
  );

  return (
    <Box
      minH="100vh"
      pt={{ base: 2, md: 3 }}
      pb={{ base: 3, md: 5 }}
      bg={COLORS.isabelline}
      backgroundImage={`linear-gradient(180deg, ${COLORS.isabelline} 0%, #EEF1F7 55%, ${COLORS.isabelline} 100%)`}
    >
      <Container maxW="container.2xl" px={{ base: 3, md: 5, xl: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          {canPreviewAsStudent && (
            <Box
              mb={{ base: 3, md: 4 }}
              p={{ base: 3, md: 4 }}
              borderRadius="xl"
              bg="white"
              border="1px solid"
              borderColor={viewAsStudent ? "orange.200" : "gray.200"}
              boxShadow="sm"
              position="relative"
              overflow="visible"
              zIndex={30}
            >
              <Flex align="center" gap={2} mb={{ base: 2, md: 2.5 }}>
                <Icon
                  as={FiEye}
                  boxSize={{ base: 3.5, md: 4 }}
                  color={viewAsStudent ? "orange.500" : "gray.500"}
                  flexShrink={0}
                />
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  fontWeight="700"
                  color="gray.800"
                  lineHeight="short"
                >
                  View as student
                </Text>
                {viewAsStudent && (
                  <Badge
                    display={{ base: "none", sm: "inline-flex" }}
                    colorScheme="orange"
                    fontSize="2xs"
                    borderRadius="md"
                    textTransform="none"
                    ml="auto"
                  >
                    Previewing
                  </Badge>
                )}
              </Flex>
              {!viewAsStudent && (
                <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" mb={{ base: 2, md: 3 }} lineHeight="tall">
                  Owners and Super Admins can open any enrolled student&apos;s portal without enrolling.
                </Text>
              )}
              <Flex
                direction={{ base: "column", md: "row" }}
                gap={{ base: 2, md: 3 }}
                align={{ base: "stretch", md: "center" }}
              >
                <Box flex="1" minW={0} fontSize={{ base: "sm", md: "md" }}>
                  <SearchableSelect
                    value={selectedStudentOption}
                    onChange={handleSelectStudent}
                    options={studentOptions}
                    isClearable
                    isLoading={loadingStudents}
                    placeholder="Search by name or email..."
                    noOptionsMessage={() => "No enrolled students found"}
                    selectedOptionStyle="check"
                    formatOptionLabel={formatStudentOptionLabel}
                    filterOption={filterStudentOption}
                    size={isMobile ? "sm" : "md"}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    chakraStyles={{
                      container: (provided) => ({ ...provided, width: "100%" }),
                      control: (provided) => ({
                        ...provided,
                        bg: "white",
                        minHeight: isMobile ? "36px" : "42px",
                        borderColor: viewAsStudent ? "orange.300" : "gray.200",
                        borderRadius: "10px",
                        fontSize: isMobile ? "13px" : "14px",
                        boxShadow: "none",
                        _hover: { borderColor: viewAsStudent ? "orange.400" : "gray.300" },
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        px: isMobile ? 2 : 3,
                        py: 0,
                      }),
                      menu: (provided) => ({
                        ...provided,
                        bg: "white",
                        zIndex: 9999,
                        fontSize: isMobile ? "13px" : "14px",
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        bg: "white",
                        color: "gray.800",
                        maxH: "320px",
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        bg: state.isFocused || state.isSelected ? "orange.50" : "white",
                        color: "gray.800",
                        fontSize: isMobile ? "13px" : "14px",
                        py: isMobile ? 2 : 2.5,
                        px: isMobile ? 2.5 : 3,
                        alignItems: "flex-start",
                        _hover: { bg: "orange.50", color: "gray.800" },
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: "gray.800",
                        fontSize: isMobile ? "13px" : "14px",
                        maxW: "100%",
                      }),
                      input: (provided) => ({
                        ...provided,
                        color: "gray.800",
                        fontSize: isMobile ? "13px" : "14px",
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "gray.400",
                        fontSize: isMobile ? "12px" : "14px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        bg: "transparent",
                        color: "gray.500",
                        px: isMobile ? 1.5 : 2,
                      }),
                      clearIndicator: (provided) => ({
                        ...provided,
                        color: "gray.400",
                        px: isMobile ? 1 : 1.5,
                      }),
                    }}
                  />
                </Box>
                {viewAsStudent && (
                  <Button
                    size={{ base: "sm", md: "md" }}
                    h={{ base: "36px", md: "42px" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    variant="solid"
                    bg={COLORS.maastrichtBlue}
                    color="white"
                    leftIcon={<Icon as={FiX} boxSize={3.5} />}
                    onClick={exitStudentPreview}
                    flexShrink={0}
                    borderRadius="10px"
                    _hover={{ bg: "#162447" }}
                    px={{ base: 3, md: 4 }}
                  >
                    Exit preview
                  </Button>
                )}
              </Flex>
            </Box>
          )}

          <Flex gap={{ base: 0, lg: 5 }} align="flex-start">
            {renderSidebar()}

            <Box flex="1" minW={0}>
              <Tabs
                index={tabIndex}
                onChange={setTabIndex}
                colorScheme="brand"
                variant="unstyled"
                isLazy
              >
                {/* Mobile / tablet: horizontal tabs with edge fade */}
                <Box
                  display={{ base: "block", lg: "none" }}
                  position="sticky"
                  top={0}
                  zIndex={10}
                  mb={3}
                  bg={COLORS.isabelline}
                  pt={1}
                  pb={1}
                >
                  <Box position="relative">
                    {navFade.left && (
                      <Box
                        position="absolute"
                        left={0}
                        top={0}
                        bottom={0}
                        w="36px"
                        zIndex={2}
                        pointerEvents="none"
                        bgGradient={`linear(to-r, ${COLORS.isabelline}, transparent)`}
                      />
                    )}
                    {navFade.right && (
                      <Box
                        position="absolute"
                        right={0}
                        top={0}
                        bottom={0}
                        w="36px"
                        zIndex={2}
                        pointerEvents="none"
                        bgGradient={`linear(to-l, ${COLORS.isabelline}, transparent)`}
                      />
                    )}
                    <TabList
                      ref={navScrollRef}
                      onScroll={updateNavFade}
                      flexWrap="nowrap"
                      gap={2}
                      overflowX="auto"
                      px={1}
                      py={1}
                      css={{
                        scrollSnapType: "x mandatory",
                        scrollPaddingInline: "8px",
                        WebkitOverflowScrolling: "touch",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                      }}
                    >
                      {NAV_ITEMS.map((item) => (
                        <Tab
                          key={item.label}
                          fontSize="2xs"
                          py={2}
                          px={2.5}
                          flexShrink={0}
                          minW="72px"
                          maxW="88px"
                          borderRadius="xl"
                          border="1.5px solid"
                          borderColor="gray.200"
                          bg="white"
                          color="gray.600"
                          fontWeight="500"
                          scrollSnapAlign="start"
                          _selected={{
                            color: COLORS.maastrichtBlue,
                            fontWeight: "700",
                            borderColor: COLORS.mandarin,
                            bg: "white",
                            boxShadow: "0 2px 8px rgba(14, 23, 53, 0.08)",
                          }}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={0.5}
                          minH="52px"
                          title={item.label}
                        >
                          <Icon as={item.icon} boxSize={4} flexShrink={0} />
                          <Text as="span" noOfLines={1} textAlign="center" w="100%" lineHeight="1.2">
                            {item.short}
                          </Text>
                        </Tab>
                      ))}
                    </TabList>
                  </Box>
                </Box>

                <Box
                  bg="white"
                  px={{ base: 4, md: 6, xl: 8 }}
                  pt={{ base: 3, md: 5, xl: 6 }}
                  pb={{ base: 4, md: 6, xl: 8 }}
                  borderRadius="2xl"
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="blackAlpha.50"
                  minH={{ lg: "640px" }}
                >
                  {loading ? (
                    <Text textAlign="center" py={8} color="gray.600" fontSize={{ base: "sm", md: "md" }}>
                      Loading...
                    </Text>
                  ) : allowTabContent ? (
                    tabPanels
                  ) : (
                    <Box textAlign="center" py={{ base: 8, md: 12 }}>
                      <Heading
                        as="h1"
                        fontSize={{ base: "1.25rem", md: "1.6rem" }}
                        color={COLORS.maastrichtBlue}
                        mb={2}
                      >
                        {getTimeOfDayGreeting()}, {greetingName}
                      </Heading>
                      <Text
                        fontSize={{ base: "sm", md: "lg" }}
                        color="gray.600"
                        mb={{ base: 4, md: 6 }}
                        px={{ base: 2, md: 0 }}
                      >
                        You haven&apos;t enrolled in any programs yet.
                      </Text>
                      <Link to="/courses-and-programs">
                        <Button
                          bg={COLORS.mandarin}
                          color="white"
                          _hover={{ bg: "#e06a38" }}
                          size={{ base: "md", md: "lg" }}
                          px={{ base: 6, md: 8 }}
                          borderRadius="xl"
                        >
                          Browse Programs
                        </Button>
                      </Link>
                    </Box>
                  )}
                </Box>
              </Tabs>
            </Box>
          </Flex>
        </motion.div>
      </Container>
    </Box>
  );
};
